<template>
  <div class="h-full bg-base-200 flex flex-col overflow-hidden">
    <!-- Header -->
    <threads-nav show-back>
      <template #center> 开通会员 </template>
    </threads-nav>

    <!-- Main Content -->
    <main class="flex-1 overflow-hidden flex flex-col">
      <!-- Plan Toggle -->
      <van-tabs v-model:active="currentPlan" @change="selectPlan">
        <van-tab title="行星构筑师" name="constructor"></van-tab>
        <van-tab title="星系幻想家" name="dreamer"></van-tab>
      </van-tabs>

      <div class="flex-1 overflow-y-auto p-4">
        <!-- Pricing Options -->
        <div class="grid grid-cols-3 gap-3 mb-6 text-center">
          <div
            v-for="(price, index) in currentPlanData.prices"
            :key="index"
            :class="[
              'card relative bg-base-100 cursor-pointer transition-all duration-300 transform hover:scale-105',
              { 'border-2 border-primary': selectedPriceIndex === index },
            ]"
            @click="selectedPriceIndex = index"
          >
            <div
              v-if="price.tag"
              :class="[
                'badge absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap',
                price.tag === '最划算' ? 'badge-secondary' : 'badge-primary',
              ]"
            >
              {{ price.tag }}
            </div>
            <div class="card-body p-3 pt-5">
              <h3 class="font-semibold">{{ price.type }}</h3>
              <p
                :class="[
                  'text-2xl font-bold my-1',
                  { 'text-primary': selectedPriceIndex === index },
                ]"
              >
                <span class="text-lg">¥</span>{{ price.price }}
              </p>
            </div>
          </div>
        </div>

        <!-- Benefits Section -->
        <div class="bg-base-100 rounded-box p-5">
          <h2 class="text-lg font-semibold mb-4 text-content-focus">
            {{ currentPlanData.name }} - 会员权益
          </h2>
          <div class="space-y-3">
            <div
              v-for="(benefit, index) in currentPlanData.benefits"
              :key="index"
              class="flex items-start"
            >
              <svg
                v-if="benefit.icon === 'check'"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="text-success flex-shrink-0"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              <svg
                v-else-if="benefit.icon === 'star'"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="text-warning flex-shrink-0"
              >
                <polygon
                  points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                ></polygon>
              </svg>
              <p class="ml-3 text-sm" v-html="benefit.text"></p>
            </div>
          </div>
        </div>

        <!-- Footer / CTA -->
        <footer class="p-4 sm:p-6 bg-base-200/80 backdrop-blur-sm">
          <!-- Payment Method -->
          <div class="mb-4">
            <h3 class="text-base font-medium mb-3">支付方式</h3>
            <div class="space-y-3">
              <div class="form-control">
                <label class="label cursor-pointer bg-base-100 rounded-box p-3">
                  <span class="label-text flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#05C160"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path
                        d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
                      ></path>
                    </svg>
                    <span class="ml-3 font-medium">微信支付</span>
                  </span>
                  <input
                    type="radio"
                    name="payment"
                    class="radio radio-primary"
                    checked
                  />
                </label>
              </div>
              <div class="form-control">
                <label
                  class="label cursor-pointer bg-base-100 rounded-box p-3 opacity-60"
                >
                  <span class="label-text flex items-center">
                    <CrystalIcon />
                    <span class="ml-3 font-medium">使用水晶支付</span>
                    <span class="ml-2 text-sm text-base-content/70">
                      余额{{ userStore.user.crystalQuantity }}
                      <span
                        class="text-primary"
                        @click="router.push({ name: 'Crystal' })"
                      >
                        去充值
                      </span>
                    </span>
                  </span>
                  <input
                    type="radio"
                    name="payment"
                    class="radio radio-primary"
                  />
                </label>
              </div>
            </div>
          </div>

          <button class="btn btn-primary btn-block text-lg h-14 rounded-full">
            确认协议并以
            <span class="font-bold">¥{{ selectedPrice }}</span> 开通
          </button>
          <div class="form-control items-center mt-3">
            <label class="label cursor-pointer">
              <input
                type="checkbox"
                :checked="true"
                class="checkbox checkbox-primary checkbox-xs mr-2"
              />
              <span class="label-text text-xs">
                我接受
                <a href="#" class="link link-primary">《会员服务协议》</a> 与
                <a href="#" class="link link-primary">《自动续费协议》</a>
              </span>
            </label>
          </div>
        </footer>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useGlobalUser } from "@/stores/globalUser";
import { useRouter } from "vue-router";
import { readMemberPlanPage } from "@/apis/user";

const userStore = useGlobalUser();
const router = useRouter();

const plansData = {
  constructor: {
    name: "行星构筑师",
    prices: [
      { type: "连续包月", price: "34.9", tag: "超值首选" },
      { type: "3个月", price: "99", tag: "" },
      { type: "12个月", price: "349", tag: "最划算" },
    ],
    benefits: [
      {
        text: '会员期间每月可计获得 <span class="font-bold text-base-content">500</span>',
        icon: "check" as const,
      },
      { text: "立即获得200能量块", icon: "check" },
      { text: "会员期间额外领取每日奖励，共计300能量块", icon: "check" },
      { text: "人物设定与记忆自动同步", icon: "check" },
      {
        text: '简单模式生成角色额度 * 1 (价值 <span class="font-bold text-base-content">50</span>)',
        icon: "check" as const,
      },
      { text: "云存储空间5GB", icon: "check" },
      { text: "行星构筑师身份标识", icon: "check" },
      { text: "免费使用标准模型，不消耗能量块", icon: "star" },
    ],
  },
  dreamer: {
    name: "星系幻想家",
    prices: [
      { type: "连续包月", price: "99", tag: "超值首选" },
      { type: "3个月", price: "279", tag: "" },
      { type: "12个月", price: "999", tag: "最划算" },
    ],
    benefits: [
      {
        text: '会员期间每月可计获得 <span class="font-bold text-base-content">1600</span>',
        icon: "check" as const,
      },
      { text: "立即获得600能量块", icon: "check" },
      { text: "会员期间额外领取每日奖励，共计1000能量块", icon: "check" },
      {
        text: '简单模式生成角色额度 * 5 (价值 <span class="font-bold text-base-content">250</span>)',
        icon: "check" as const,
      },
      { text: "云存储空间100GB", icon: "check" },
      { text: "星系幻想家身份标识", icon: "star" },
      { text: "免费使用标准模型、进阶模型，不消耗能量块", icon: "star" },
      {
        text: '免费更换小屋皮肤 (价值 <span class="font-bold text-base-content">30</span>)',
        icon: "star" as const,
      },
      {
        text: '人物设定与记忆自动同步，解锁所有记忆卡槽 (价值 <span class="font-bold text-base-content">90</span>)',
        icon: "star" as const,
      },
    ],
  },
};

// 响应式状态
const currentPlan = ref<keyof typeof plansData>("constructor");
const selectedPriceIndex = ref(0);

// 计算属性
const currentPlanData = computed(() => plansData[currentPlan.value]);
const selectedPrice = computed(
  () => currentPlanData.value.prices[selectedPriceIndex.value].price
);

// 方法
const selectPlan = (plan: keyof typeof plansData) => {
  currentPlan.value = plan;
  selectedPriceIndex.value = 0; // 重置价格选择
};

onMounted(async () => {
  const res = await readMemberPlanPage();
  console.log(res);
});
</script>
